/**
 * Super Admin → Firebase shard management.
 *
 * Each Firebase project caps at 30 apps. A Bizil tenant burns 4 apps
 * (customer Android+iOS + rider Android+iOS), so 7 tenants per project
 * is the safe cap (28 of 30, with 2 slots free for future variants).
 *
 * From here a super-admin can:
 *  - Add a new Firebase shard (paste the project's service-account JSON).
 *  - See capacity utilisation per shard.
 *  - Assign or auto-pick a shard for any tenant.
 *  - Trigger the auto-provisioner that creates the 4 Firebase apps for a tenant.
 *  - Save the platform-wide VAPID keypair for browser web push.
 */
import { useEffect, useRef, useState } from "react";
import { Flame, Plus, RefreshCcw, Trash2, Zap, KeyRound, Loader2, ChevronRight, Volume2, Upload, Play, BellRing } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function SuperFirebase() {
    const [shards, setShards] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [showVapid, setShowVapid] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get("/super/firebase/projects"),
                api.get("/super/tenants"),
            ]);
            setShards(a.data || []);
            // iter134w — filter out soft-deleted tenants from the
            // Firebase page entirely. The ``/super/tenants`` endpoint
            // deliberately returns DELETED rows so the SuperTenants
            // page can offer a "Show deleted" toggle for recovery,
            // but on this page they're pure noise: a deleted tenant's
            // shard slot is still counted, and its row shows up in
            // "Unassigned tenants" with a live Provision button that
            // would resurrect a Firebase config for a dead tenant.
            const live = (b.data || []).filter(
                (t) => t?.status !== "DELETED" && !t?.deleted_at,
            );
            setTenants(live);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load Firebase shards");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const removeShard = async (sid) => {
        if (!window.confirm("Remove this Firebase shard? Tenants assigned to it must be reassigned first.")) return;
        setBusyId(sid);
        try {
            await api.delete(`/super/firebase/projects/${sid}`);
            toast.success("Shard removed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
        } finally {
            setBusyId("");
        }
    };

    const totalSlots = shards.reduce((a, s) => a + (s.max_tenants || 7), 0);
    const usedSlots = shards.reduce((a, s) => a + (s.assigned_tenants || 0), 0);

    return (
        <div className="space-y-6" data-testid="super-firebase-page">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-black flex items-center gap-2">
                        <Flame size={22} className="text-orange-500" /> Firebase shards
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Each shard caps at 30 apps. Bizil burns 4 apps per tenant (customer + rider × Android + iOS),
                        so each shard holds <span className="font-semibold">~7 tenants</span>. Add a new shard before you run out.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button data-testid="super-firebase-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-card border border-border hover:bg-muted">
                        <RefreshCcw size={13} /> Refresh
                    </button>
                    <button data-testid="super-firebase-vapid" onClick={() => setShowVapid(true)} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-card border border-border hover:bg-muted">
                        <KeyRound size={13} /> VAPID keys
                    </button>
                    <button data-testid="super-firebase-add" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-foreground text-background hover:opacity-90">
                        <Plus size={13} /> Add shard
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Stat label="Shards" value={shards.length} testid="firebase-stat-shards" />
                <Stat label="Tenants placed" value={`${usedSlots} / ${totalSlots || "0"}`} testid="firebase-stat-tenants" />
                <Stat label="Open slots" value={Math.max(0, totalSlots - usedSlots)} testid="firebase-stat-open" />
            </div>

            {loading && <p className="text-sm text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading…</p>}

            {!loading && shards.length === 0 && (
                <div className="text-center py-16 bg-card border border-dashed border-border rounded-2xl" data-testid="firebase-empty">
                    <Flame className="mx-auto text-muted-foreground" size={26} />
                    <p className="mt-2 font-bold">No Firebase shards yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Create a Firebase project on https://console.firebase.google.com, generate a service-account JSON,
                        and click "Add shard" above.
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {shards.map((s) => (
                    <ShardCard key={s.id} shard={s} tenants={tenants}
                               busy={busyId === s.id} onRemove={() => removeShard(s.id)}
                               onAfterAction={load} />
                ))}
            </div>

            {!loading && shards.length > 0 && (
                <UnassignedTenants tenants={tenants} shards={shards} onAfterAction={load} />
            )}

            <NotificationSoundCard />

            {showAdd && <AddShardModal onClose={() => setShowAdd(false)} onSaved={load} />}
            {showVapid && <VapidModal onClose={() => setShowVapid(false)} />}
        </div>
    );
}


function UnassignedTenants({ tenants, shards, onAfterAction }) {
    // Tenants without a firebase_project_id can't push notifications and
    // their tenant-specific mobile builds will be aborted by the build
    // script. Surface them prominently so the super-admin can provision
    // them in one click. ``provision`` will auto-assign a shard via the
    // backend's round-robin assigner when ``firebase_project_id`` is null.
    const unassigned = tenants.filter((t) => !t.firebase_project_id);
    const hasCapacity = shards.some((s) => (s.available_slots ?? 0) > 0
        && s.service_account_configured);
    if (unassigned.length === 0) return null;
    return (
        <div
            data-testid="firebase-unassigned-section"
            className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4"
        >
            <div className="flex items-center gap-2 mb-2">
                <Zap size={16} className="text-amber-700" />
                <h3 className="font-black text-amber-900">
                    Unassigned tenants ({unassigned.length})
                </h3>
            </div>
            <p className="text-xs text-amber-800 mb-3">
                These tenants don&apos;t have Firebase apps yet. Their mobile
                builds will fail until you provision. Clicking <b>Provision</b>
                {" "}auto-assigns the next shard with capacity, creates the 4
                Firebase apps (customer + rider × Android + iOS), and caches
                the per-app config blobs on the tenant.
            </p>
            {!hasCapacity && (
                <p className="text-xs text-rose-700 font-semibold mb-3"
                   data-testid="firebase-no-capacity-warning">
                    ⚠ No shard has open slots + a valid service-account JSON.
                    Add a shard or fix an existing one before provisioning.
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {unassigned.map((t) => (
                    <TenantRow
                        key={t.id}
                        tenant={t}
                        shardId={null}
                        onAfterAction={onAfterAction}
                        disabled={!hasCapacity}
                        pickableShards={shards.filter((s) => (s.available_slots ?? 0) > 0 && s.service_account_configured)}
                    />
                ))}
            </div>
        </div>
    );
}


function Stat({ label, value, testid }) {
    return (
        <div data-testid={testid} className="bg-card border border-border rounded-2xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
            <p className="text-2xl font-black mt-1">{value}</p>
        </div>
    );
}


function ShardCard({ shard, tenants, busy, onRemove, onAfterAction }) {
    const placed = tenants.filter((t) => t.firebase_project_id === shard.id);
    const full = shard.available_slots <= 0;
    const [replacing, setReplacing] = useState(false);
    const [newSaJson, setNewSaJson] = useState("");
    const [saving, setSaving] = useState(false);

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const txt = await f.text();
        setNewSaJson(txt);
    };

    const saveReplacement = async (allowProjectIdChange = false) => {
        if (!newSaJson.trim()) { toast.error("Paste or upload a Service Account JSON first"); return; }
        // iter77 — Use the rehydrate path (existing_shard_id) so the
        // shard's UUID stays the same (placed tenants pinned to it
        // are unchanged). If the SA's project_id differs from the
        // shard's current project_id, the backend returns 409 with
        // structured detail; we surface a confirmation and retry
        // with allow_project_id_change=true.
        setSaving(true);
        try {
            await api.post("/super/firebase/projects", {
                existing_shard_id: shard.id,
                project_id: shard.project_id,  // ignored on rehydrate path
                name: shard.name,
                max_tenants: shard.max_tenants,
                service_account_json: newSaJson,
                allow_project_id_change: allowProjectIdChange,
            });
            toast.success(`Shard updated — placed tenants unchanged, new credentials live.`);
            setReplacing(false);
            setNewSaJson("");
            onAfterAction?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // The backend wraps the structured payload in ``detail``
            // when raising HTTPException(409, {...dict}). FastAPI
            // forwards it as ``detail`` on the response.
            if (detail && typeof detail === "object" && detail.error === "project_id_mismatch") {
                const confirmed = window.confirm(
                    `The Service Account you uploaded is for Firebase project ` +
                    `"${detail.file_project_id}", but this shard's current project_id is ` +
                    `"${detail.shard_project_id}".\n\n` +
                    `Click OK to UPDATE the shard's project_id to "${detail.file_project_id}". ` +
                    `Placed tenants (${placed.length}) stay assigned — only the Firebase ` +
                    `project they route to will change.\n\n` +
                    `Click Cancel if you uploaded the wrong file.`,
                );
                if (confirmed) {
                    // Recurse with the override flag.
                    setSaving(false);
                    return saveReplacement(true);
                }
            } else {
                toast.error((typeof detail === "string" ? detail : "Replace failed"));
            }
        } finally { setSaving(false); }
    };

    return (
        <div className="bg-card border border-border rounded-2xl px-4 py-4" data-testid={`firebase-shard-${shard.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{shard.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{shard.project_id}</span>
                        {full && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">Full</span>}
                        {!shard.service_account_configured && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">No JSON</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        {shard.assigned_tenants} / {shard.max_tenants} tenants · {shard.available_slots} slot(s) open
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`firebase-shard-${shard.id}-replace`}
                        onClick={() => setReplacing((x) => !x)}
                        disabled={busy}
                        className="text-xs text-indigo-700 hover:text-indigo-900 disabled:opacity-40 inline-flex items-center gap-1"
                        title="Replace the Service Account JSON without losing placed tenants"
                    >
                        <KeyRound size={13} /> Replace SA JSON
                    </button>
                    <button
                        data-testid={`firebase-shard-${shard.id}-remove`}
                        onClick={onRemove}
                        disabled={busy || placed.length > 0}
                        className="text-xs text-rose-600 hover:text-rose-800 disabled:opacity-40 inline-flex items-center gap-1"
                        title={placed.length > 0 ? "Reassign tenants first" : "Remove shard"}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            {replacing && (
                <div className="mt-3 pt-3 border-t border-border space-y-2"
                     data-testid={`firebase-shard-${shard.id}-replace-form`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                        Replace Service Account JSON for {shard.project_id}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                        Use this if the current SA is invalid (e.g. missing <code className="text-[10px]">token_uri</code>),
                        the SA key was rotated, or you uploaded a placeholder by mistake.
                        Placed tenants ({placed.length}) stay assigned — only the credentials used to mint
                        OAuth tokens for FCM v1 sends are updated.
                    </p>
                    <input
                        type="file" accept="application/json,.json"
                        onChange={onFileChosen}
                        className="block text-[11px]"
                        data-testid={`firebase-shard-${shard.id}-replace-file`}
                    />
                    <textarea
                        value={newSaJson}
                        onChange={(e) => setNewSaJson(e.target.value)}
                        rows={4}
                        placeholder='Or paste the full service-account JSON here (must include token_uri, private_key, client_email, project_id)'
                        className="w-full font-mono text-[10px] px-2 py-1.5 rounded border border-border bg-card"
                        data-testid={`firebase-shard-${shard.id}-replace-text`}
                    />
                    <div className="flex items-center gap-2">
                        <button
                            data-testid={`firebase-shard-${shard.id}-replace-save`}
                            disabled={saving || !newSaJson.trim()}
                            onClick={() => saveReplacement(false)}
                            className="flex-1 text-[11px] font-bold px-3 py-1.5 rounded bg-foreground text-background disabled:opacity-50 inline-flex items-center justify-center gap-1"
                        >
                            {saving ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
                            Validate &amp; replace
                        </button>
                        <button
                            onClick={() => { setReplacing(false); setNewSaJson(""); }}
                            className="text-[11px] px-3 py-1.5 rounded border border-border hover:bg-muted"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-3 pt-3 border-t border-border">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Placed tenants</p>
                {placed.length === 0 && <p className="text-xs text-muted-foreground">None yet — auto-assign during the next provision call.</p>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {placed.map((t) => (
                        <TenantRow key={t.id} tenant={t} shardId={shard.id} onAfterAction={onAfterAction} />
                    ))}
                </div>
            </div>
        </div>
    );
}


function TenantRow({ tenant, shardId, onAfterAction, disabled = false, pickableShards = null }) {
    const [busy, setBusy] = useState("");
    // iter146 — "" = auto-pick (existing round-robin behaviour, unchanged
    // default). When the operator explicitly picks a shard here, we
    // call the (already-existing, previously unused-by-any-UI)
    // POST /firebase/assign endpoint with that shard_id FIRST, pinning
    // the tenant to it, before /firebase/provision runs — provision()
    // reuses whatever shard is already assigned rather than re-picking.
    // This is what lets a super admin target a tenant's Firebase apps
    // at a SPECIFIC newly-added project instead of an opaque auto-pick.
    const [chosenShardId, setChosenShardId] = useState("");
    const [showNewOrderSound, setShowNewOrderSound] = useState(false);
    const provision = async () => {
        setBusy("prov");
        try {
            if (chosenShardId) {
                await api.post(`/super/tenants/${tenant.id}/firebase/assign`, { shard_id: chosenShardId });
            }
            await api.post(`/super/tenants/${tenant.id}/firebase/provision`);
            toast.success(`Provisioned ${tenant.name} → 4 Firebase apps`);
            onAfterAction?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Provision failed");
        } finally { setBusy(""); }
    };
    // Each tenant needs 4 Firebase configs (customer+rider × Android+iOS).
    // Count how many are cached on the tenant doc so the badge can show
    // "Provisioned" / "Partial (N/4)" / "Not provisioned" at a glance.
    const slots = [
        { key: "google_services_json_customer", label: "Customer · Android" },
        { key: "google_services_json_rider",    label: "Rider · Android" },
        { key: "ios_plist_customer",            label: "Customer · iOS" },
        { key: "ios_plist_rider",               label: "Rider · iOS" },
    ];
    const filled = slots.filter((s) => !!tenant[s.key]).length;
    const fullyProvisioned = filled === 4;
    const someProvisioned = filled > 0 && filled < 4;
    const badge = fullyProvisioned
        ? { label: "Provisioned", tone: "bg-emerald-100 text-emerald-700" }
        : someProvisioned
            ? { label: `Partial · ${filled}/4`, tone: "bg-amber-100 text-amber-700" }
            : { label: "Not provisioned", tone: "bg-muted text-muted-foreground" };
    const tooltip = slots.map((s) => `${tenant[s.key] ? "✓" : "·"}  ${s.label}`).join("\n");

    // iter134w — surface FROZEN (SUSPENDED) tenants so the super-admin
    // can see at a glance which Firebase project + app IDs are stuck
    // to an inactive tenant. The Firebase project apps still exist on
    // the console (consuming a shard slot), but the tenant has been
    // frozen — often the operator's next question is "which Firebase
    // project do I need to delete / migrate?". Showing the project_id
    // and appIds inline makes cleanup a one-glance operation.
    const isFrozen = tenant?.status === "SUSPENDED";
    // Pull one representative Firebase Project ID from any cached
    // per-app config blob. All 4 configs are minted against the same
    // Firebase project so the first one we find is authoritative.
    const projectIdFromConfig = (() => {
        // Android configs are JSON strings — try to parse.
        for (const k of ["google_services_json_customer", "google_services_json_rider"]) {
            const raw = tenant?.[k];
            if (raw && typeof raw === "string") {
                try {
                    const j = JSON.parse(raw);
                    const pid = j?.project_info?.project_id;
                    if (pid) return pid;
                } catch { /* not JSON — ignore */ }
            }
        }
        // iOS configs are XML plists — regex out ``PROJECT_ID``.
        for (const k of ["ios_plist_customer", "ios_plist_rider"]) {
            const raw = tenant?.[k];
            if (raw && typeof raw === "string") {
                const m = raw.match(/<key>PROJECT_ID<\/key>\s*<string>([^<]+)<\/string>/);
                if (m) return m[1];
            }
        }
        return null;
    })();
    // Also collect the 4 App IDs (Android package name + iOS bundle
    // id per role) so the operator can identify exactly which apps
    // to delete in the Firebase console when winding down a frozen
    // tenant. Best-effort — silent no-op when the tenant has no
    // cached config.
    const appIds = (() => {
        const out = {};
        try {
            const rawC = tenant?.google_services_json_customer;
            if (rawC) {
                const j = JSON.parse(rawC);
                const pkg = j?.client?.[0]?.client_info?.android_client_info?.package_name;
                if (pkg) out.androidCustomer = pkg;
            }
        } catch { /* ignore */ }
        try {
            const rawR = tenant?.google_services_json_rider;
            if (rawR) {
                const j = JSON.parse(rawR);
                const pkg = j?.client?.[0]?.client_info?.android_client_info?.package_name;
                if (pkg) out.androidRider = pkg;
            }
        } catch { /* ignore */ }
        for (const [k, key] of [["iosCustomer", "ios_plist_customer"], ["iosRider", "ios_plist_rider"]]) {
            const raw = tenant?.[key];
            if (raw && typeof raw === "string") {
                const m = raw.match(/<key>BUNDLE_ID<\/key>\s*<string>([^<]+)<\/string>/);
                if (m) out[k] = m[1];
            }
        }
        return out;
    })();

    return (
        <div>
        <div
            className={`bg-background border rounded-xl px-3 py-2 flex items-center justify-between gap-2 flex-wrap ${isFrozen ? "border-sky-300 bg-sky-50/40" : "border-border"}`}
            data-testid={`firebase-tenant-${tenant.id}`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-semibold truncate">{tenant.name}</p>
                    {isFrozen && (
                        <span
                            data-testid={`firebase-tenant-${tenant.id}-frozen-badge`}
                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200"
                            title="Tenant is SUSPENDED — Firebase apps are still consuming shard capacity. Consider deleting the project to reclaim the slot."
                        >
                            Frozen
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <p className="text-[11px] text-muted-foreground truncate">{tenant.slug}</p>
                    <span
                        title={tooltip}
                        data-testid={`firebase-tenant-${tenant.id}-status`}
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${badge.tone}`}
                    >
                        {badge.label}
                    </span>
                </div>
                {isFrozen && (projectIdFromConfig || Object.keys(appIds).length > 0) && (
                    <div
                        data-testid={`firebase-tenant-${tenant.id}-frozen-details`}
                        className="mt-1.5 text-[10px] text-sky-900 bg-sky-100/60 border border-sky-200 rounded-md px-2 py-1 leading-snug space-y-0.5"
                    >
                        {projectIdFromConfig && (
                            <p>
                                <span className="font-bold">Firebase project:</span>{" "}
                                <code className="font-mono">{projectIdFromConfig}</code>{" "}
                                <span className="text-sky-700">
                                    ({projectIdFromConfig}.firebaseapp.com)
                                </span>
                            </p>
                        )}
                        {appIds.androidCustomer && (
                            <p><span className="font-bold">Customer · Android:</span> <code className="font-mono">{appIds.androidCustomer}</code></p>
                        )}
                        {appIds.androidRider && (
                            <p><span className="font-bold">Rider · Android:</span> <code className="font-mono">{appIds.androidRider}</code></p>
                        )}
                        {appIds.iosCustomer && (
                            <p><span className="font-bold">Customer · iOS:</span> <code className="font-mono">{appIds.iosCustomer}</code></p>
                        )}
                        {appIds.iosRider && (
                            <p><span className="font-bold">Rider · iOS:</span> <code className="font-mono">{appIds.iosRider}</code></p>
                        )}
                    </div>
                )}
            </div>
            {pickableShards && pickableShards.length > 0 && (
                <select
                    data-testid={`firebase-tenant-${tenant.id}-shard-picker`}
                    value={chosenShardId}
                    onChange={(e) => setChosenShardId(e.target.value)}
                    disabled={!!busy || disabled}
                    title="Choose which Firebase project this tenant's apps get created in — leave on Auto-pick to use whichever shard has the most open capacity"
                    className="text-[10px] px-1.5 py-1 rounded-md border border-border bg-card shrink-0 max-w-[140px]"
                >
                    <option value="">Auto-pick shard</option>
                    {pickableShards.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name} ({s.available_slots} open)
                        </option>
                    ))}
                </select>
            )}
            <button
                data-testid={`firebase-tenant-${tenant.id}-provision`}
                onClick={provision}
                disabled={!!busy || disabled}
                title={
                    disabled
                        ? "No shard capacity — add a shard or free up space first"
                        : fullyProvisioned
                            ? "Re-create all 4 Firebase apps + refresh cached configs"
                            : "Create the 4 Firebase apps for this tenant"
                }
                className="text-[11px] font-bold px-2 py-1 rounded-md bg-foreground text-background hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
            >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                {fullyProvisioned ? "Re-provision" : "Provision"}
            </button>
            <button
                type="button"
                data-testid={`firebase-tenant-${tenant.id}-new-order-sound-toggle`}
                onClick={() => setShowNewOrderSound((v) => !v)}
                title="Configure a distinct new-order alert sound / loud mode for this tenant only"
                className="text-[11px] font-bold px-2 py-1 rounded-md border border-border hover:bg-muted inline-flex items-center gap-1 shrink-0"
            >
                <BellRing size={11} />
                New order alert
            </button>
        </div>
        {showNewOrderSound && (
            <div className="mt-2">
                <TenantNewOrderSoundPanel tenant={tenant} />
            </div>
        )}
        </div>
    );
}


function AddShardModal({ onClose, onSaved }) {
    const [projectId, setProjectId] = useState("");
    const [name, setName] = useState("");
    const [json, setJson] = useState("");
    const [maxTenants, setMaxTenants] = useState(7);
    const [busy, setBusy] = useState(false);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const txt = await f.text();
        setJson(txt);
        try {
            const parsed = JSON.parse(txt);
            if (parsed.project_id && !projectId) setProjectId(parsed.project_id);
            if (parsed.project_id && !name) setName(`${parsed.project_id} shard`);
        } catch (err) { console.debug("[AddShard] paste JSON parse failed:", err); }
    };

    const submit = async () => {
        if (!projectId || !name || !json) {
            toast.error("Project ID, name and service-account JSON are required");
            return;
        }
        setBusy(true);
        try {
            await api.post("/super/firebase/projects", {
                project_id: projectId, name, service_account_json: json,
                max_tenants: Number(maxTenants) || 7,
            });
            toast.success("Shard added");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid="firebase-add-modal">
            <div className="bg-background border border-border rounded-2xl w-full max-w-lg p-5 space-y-3">
                <h2 className="font-black text-lg">Add Firebase shard</h2>
                <Field label="Firebase Project ID" testid="firebase-add-pid">
                    <input data-testid="firebase-add-pid-input" value={projectId} onChange={(e) => setProjectId(e.target.value)}
                           placeholder="my-firebase-project" className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm" />
                </Field>
                <Field label="Display name" testid="firebase-add-name">
                    <input data-testid="firebase-add-name-input" value={name} onChange={(e) => setName(e.target.value)}
                           placeholder="Shard 1" className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm" />
                </Field>
                <Field label="Max tenants per shard (default 7)" testid="firebase-add-max">
                    <input data-testid="firebase-add-max-input" type="number" min="1" max="10" value={maxTenants}
                           onChange={(e) => setMaxTenants(e.target.value)}
                           className="w-32 px-3 py-2 rounded-lg border border-border bg-card text-sm" />
                </Field>
                <Field label="Service account JSON" testid="firebase-add-json">
                    <input data-testid="firebase-add-json-file" type="file" accept="application/json" onChange={onFile}
                           className="block text-xs mb-2" />
                    <textarea data-testid="firebase-add-json-text" value={json} onChange={(e) => setJson(e.target.value)} rows={6}
                              placeholder='{"type":"service_account",...}'
                              className="w-full font-mono text-xs px-3 py-2 rounded-lg border border-border bg-card" />
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="firebase-add-cancel" onClick={onClose} disabled={busy}
                            className="text-xs font-bold px-3 py-2 rounded-lg bg-card border border-border">Cancel</button>
                    <button data-testid="firebase-add-save" onClick={submit} disabled={busy}
                            className="text-xs font-bold px-3 py-2 rounded-lg bg-foreground text-background inline-flex items-center gap-1">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                        Add shard
                    </button>
                </div>
            </div>
        </div>
    );
}


function VapidModal({ onClose }) {
    const [pub, setPub] = useState("");
    const [priv, setPriv] = useState("");
    const [contact, setContact] = useState("mailto:noreply@bizil.app");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!pub || !priv) return toast.error("Both keys are required");
        setBusy(true);
        try {
            await api.post("/super/firebase/vapid", { public_key: pub, private_key: priv, contact_email: contact });
            toast.success("VAPID keys saved");
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" data-testid="firebase-vapid-modal">
            <div className="bg-background border border-border rounded-2xl w-full max-w-lg p-5 space-y-3">
                <h2 className="font-black text-lg">Web Push VAPID keys</h2>
                <p className="text-xs text-muted-foreground">
                    Generate one with <code className="bg-muted px-1 rounded">npx web-push generate-vapid-keys</code> and paste below. Used across all tenants for browser push.
                </p>
                <Field label="Public key" testid="firebase-vapid-pub">
                    <input data-testid="firebase-vapid-pub-input" value={pub} onChange={(e) => setPub(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-card text-xs font-mono" />
                </Field>
                <Field label="Private key" testid="firebase-vapid-priv">
                    <input data-testid="firebase-vapid-priv-input" value={priv} onChange={(e) => setPriv(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-card text-xs font-mono" />
                </Field>
                <Field label="Contact email (mailto:…)" testid="firebase-vapid-contact">
                    <input data-testid="firebase-vapid-contact-input" value={contact} onChange={(e) => setContact(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-card text-xs" />
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="firebase-vapid-cancel" onClick={onClose} disabled={busy} className="text-xs font-bold px-3 py-2 rounded-lg bg-card border border-border">Cancel</button>
                    <button data-testid="firebase-vapid-save" onClick={submit} disabled={busy} className="text-xs font-bold px-3 py-2 rounded-lg bg-foreground text-background">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1">{label}</label>
            {children}
        </div>
    );
}


/**
 * Generic platform-wide push-sound upload card — parameterized so the
 * general notification sound and the new-order alert sound (below)
 * share one implementation instead of ~180 duplicated lines. Each
 * instance is uploaded once by Super Admin and bundled into every
 * APK / IPA build via ``scripts/download_notification_sound.py``.
 *
 * IMPORTANT: FCM custom sounds are NOT a runtime asset on Android or
 * iOS — the audio file must physically live in the app bundle (Android
 * ``res/raw`` / iOS ``Sounds``). Uploading via this card only stores
 * the master file and updates the version stamp; you still need to
 * REBUILD the APK / IPA to ship the new sound to devices. The build
 * script handles the file-copy step.
 */
function SoundUploadCard({
    endpoint, testidPrefix, icon, iconGradient, title, description,
    emptyText, channelNote, loudModeEndpoint, deployTenantSlug, durationTip,
}) {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [loudBusy, setLoudBusy] = useState(false);
    const fileRef = useRef(null);
    const audioRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(endpoint);
            setCfg(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load sound config");
        } finally {
            setLoading(false);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [endpoint]);

    const toggleLoudMode = async () => {
        if (!loudModeEndpoint) return;
        const next = !cfg?.loud_mode_enabled;
        setLoudBusy(true);
        try {
            await api.patch(loudModeEndpoint, { enabled: next });
            setCfg((c) => ({ ...c, loud_mode_enabled: next }));
            toast.success(next ? "Loud/repeated alert enabled for this tenant" : "Loud/repeated alert disabled");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
        } finally {
            setLoudBusy(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["mp3", "wav", "ogg", "m4a", "aac", "caf", "aiff"].includes(ext)) {
            toast.error("Use .mp3, .wav, .m4a, .ogg, or .caf");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Keep the file under 5 MB so APKs stay small");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Sound saved (v${data.version}). Rebuild APK + IPA to ship it.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const clear = async () => {
        if (!window.confirm("Reset this sound? Devices will revert on next app launch (or next build).")) return;
        setBusy(true);
        try {
            await api.delete(endpoint);
            toast.success("Reset");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid={`${testidPrefix}-card`}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconGradient} inline-flex items-center justify-center text-white shrink-0`}>
                    {icon}
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-lg">{title}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                </div>
            </div>

            {/* Always-visible upload requirements — previously this info only
                existed buried inside the collapsed "After upload, deploy
                with" <details> below the upload button, so an admin picked
                a file before ever seeing the iOS format restriction
                (confirmed against fcm.py: only .wav/.aiff/.caf ever reach
                aps.sound — anything else is silently dropped and the
                device plays the system default instead of the uploaded
                file). Surfacing it here, before the button, so the right
                file gets picked the first time. */}
            <div
                className="mt-3 bg-muted/30 border border-border rounded-xl px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground space-y-1.5"
                data-testid={`${testidPrefix}-requirements`}
            >
                <p className="font-bold text-foreground text-xs">Upload requirements</p>
                <p>
                    <strong className="text-foreground">Format:</strong> <code>.wav</code> is safest — it works on
                    both Android and iOS. <code>.mp3</code>, <code>.m4a</code>, <code>.aac</code>, <code>.ogg</code>,
                    <code> .aiff</code> and <code>.caf</code> are also accepted, but <strong className="text-foreground">iOS
                    only ever plays .wav, .aiff, or .caf</strong> — any other format is silently ignored on iPhone
                    and the system default tone plays instead.
                </p>
                <p><strong className="text-foreground">Max file size:</strong> 5 MB.</p>
                <p>
                    <strong className="text-foreground">Duration:</strong> {durationTip} iOS hard-caps custom
                    notification sounds at 30 seconds — anything longer is ignored and the system default plays
                    instead (Apple platform limit, not enforced by this upload).
                </p>
                <p>
                    <strong className="text-foreground">Quality:</strong> normalise loudness to around −3 dBFS so
                    it cuts through background noise; mono is fine and keeps the file small — no need for stereo.
                </p>
                <p>
                    <strong className="text-foreground">Android:</strong> all the formats above work, and each
                    upload automatically registers a fresh notification channel version, so existing installs
                    pick up the new sound on their next update with no extra action from you.
                </p>
                <p>
                    The file ships to devices exactly as uploaded — there&apos;s no server-side conversion, so
                    getting the format right before uploading matters.
                </p>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground mt-4">
                    <Loader2 className="inline animate-spin mr-2" size={14}/>Loading…
                </p>
            ) : (
                <div className="mt-4 space-y-3">
                    {loudModeEndpoint && (
                        <label
                            className="flex items-center justify-between gap-3 bg-muted/50 rounded-xl p-3 cursor-pointer"
                            data-testid={`${testidPrefix}-loud-mode-row`}
                        >
                            <span className="min-w-0">
                                <span className="block text-sm font-bold">Loud / repeated alert</span>
                                <span className="block text-[11px] text-muted-foreground">
                                    Longer, more insistent chime + vibration for new-order pushes on THIS tenant only —
                                    independent of whether a distinct sound file is set above.
                                </span>
                            </span>
                            <span className="relative inline-flex items-center shrink-0">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={!!cfg?.loud_mode_enabled}
                                    disabled={loudBusy}
                                    onChange={toggleLoudMode}
                                    data-testid={`${testidPrefix}-loud-mode-toggle`}
                                />
                                <span className="w-9 h-5 rounded-full bg-muted-foreground/30 peer-checked:bg-primary transition-colors" />
                                <span className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                            </span>
                        </label>
                    )}
                    {cfg?.filename ? (
                        <div className="bg-muted/50 rounded-xl p-3 flex items-center gap-3" data-testid={`${testidPrefix}-current`}>
                            <button
                                type="button"
                                onClick={() => audioRef.current?.play?.()}
                                className="w-10 h-10 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center hover:scale-105 transition"
                                data-testid={`${testidPrefix}-preview`}
                                aria-label="Preview"
                            >
                                <Play size={16} />
                            </button>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-sm truncate">{cfg.original_filename || cfg.filename}</p>
                                <p className="text-[11px] text-muted-foreground">
                                    v{cfg.version} · {Math.round((cfg.size_bytes || 0) / 1024)} KB
                                    {cfg.updated_at && <> · {new Date(cfg.updated_at).toLocaleString()}</>}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={clear}
                                disabled={busy}
                                className="text-xs font-bold text-rose-600 hover:underline disabled:opacity-50"
                                data-testid={`${testidPrefix}-clear`}
                            >
                                Reset
                            </button>
                            <audio ref={audioRef} src={cfg.url} preload="none" />
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground" data-testid={`${testidPrefix}-empty`}>{emptyText}</p>
                    )}

                    <input
                        ref={fileRef}
                        type="file"
                        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.caf,.aiff"
                        className="hidden"
                        onChange={(e) => { upload(e.target.files?.[0]); }}
                        data-testid={`${testidPrefix}-file`}
                    />
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current?.click(); }}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background font-bold rounded-xl px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
                        data-testid={`${testidPrefix}-upload`}
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {cfg?.filename ? "Replace sound" : "Upload custom sound"}
                    </button>

                    <details className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
                        <summary className="cursor-pointer font-bold text-foreground">After upload, deploy with:</summary>
                        {deployTenantSlug ? (
                            <>
                                <pre className="mt-2 text-[11px] leading-snug whitespace-pre-wrap">{`# Rebuild THIS tenant's own APK / IPA — the sound is bundled per-build,
# not baked into the shared JS bundle (see note below).
python scripts/build_customer_app.py --tenant ${deployTenantSlug} --build
python scripts/build_rider_app.py --tenant ${deployTenantSlug} --build`}</pre>
                                <p className="mt-2">
                                    <strong>No frontend deploy needed for the on/off decision.</strong> Unlike
                                    the platform-wide sound below, this tenant's app fetches its own
                                    <code> new_order_alert_config</code> at runtime (same call the live app
                                    already makes for branding), so whether the distinct channel is even
                                    used updates immediately — no rebuild required. A native rebuild is
                                    only needed to bundle the actual audio file into <em>this tenant's</em> own
                                    compiled APK / IPA (each tenant ships a separately-compiled app, so the
                                    bytes at that resource name can genuinely differ per tenant) — until
                                    then, devices fall back to the general sound / system default, exactly
                                    as if nothing were configured.
                                </p>
                            </>
                        ) : (
                            <pre className="mt-2 text-[11px] leading-snug whitespace-pre-wrap">{`# 1. Bundle the file + regenerate the JS module (must run BEFORE the build)
BIZIL_API_BASE=https://food.localappstore.in \\
  python scripts/download_notification_sound.py

# 2. Build the web bundle FIRST, then sync to native projects
cd frontend && npm run build
npx cap sync android && npx cap sync ios

# 3. Rebuild + upload
# Android: ./gradlew assembleRelease
# iOS: open ios/App/App.xcworkspace and Archive`}</pre>
                        )}
                        {!deployTenantSlug && (
                            <p className="mt-2">
                                <strong>Step ordering matters.</strong> The Python script writes
                                <code> frontend/src/lib/_generated_notification.js</code> which exports the
                                sound version + filename. <code>npm run build</code> bundles those constants
                                into the JS. <code>cap sync</code> then copies the freshly-built JS into
                                both native projects. Running steps out of order means the previous
                                JS bundle gets shipped — and the channel registers with sound="default".
                                This platform-wide sound is the only one baked into the shared JS module —
                                each tenant's own new-order alert sound is rebuilt per-tenant instead (see
                                that panel's own deploy instructions).
                            </p>
                        )}
                        <p className="mt-2">
                            <strong>Got an MP3 for iOS?</strong> Convert it first — see the format
                            requirement above. <code>afconvert source.mp3 dest.caf -d ima4 -f caff -v</code> produces
                            a <code>.caf</code> Apple will actually play.
                        </p>
                        <p className="mt-2">
                            <strong>Existing installs:</strong> older APK builds advertise
                            <code> sound_version=0</code> at FCM-token registration → backend
                            keeps dispatching them to {channelNote} with the system default tone
                            (no breakage). Once they upgrade to the rebuilt APK they start hearing
                            the custom sound automatically — no "clear app data" workaround needed.
                        </p>
                    </details>
                </div>
            )}
        </div>
    );
}

function NotificationSoundCard() {
    return (
        <SoundUploadCard
            endpoint="/super/platform-settings/notification-sound"
            testidPrefix="notif-sound"
            icon={<Volume2 size={18} />}
            iconGradient="from-indigo-500 to-purple-500"
            title="Push notification sound"
            description="One platform-wide sound that plays on Android + iOS for routine notifications (status updates, etc). Falls back to the system default if not configured. After upload, rebuild the APK / IPA — Android channel settings are immutable so we auto-bump the channel ID on every upload to force a fresh registration."
            emptyText="No custom sound configured — devices play the system default tone."
            channelNote={<>the stable <code>bizil_orders_high_v2</code> channel</>}
            durationTip="Keep it short — 1–3 seconds, a brief chime is enough for a routine status update."
        />
    );
}

/**
 * New-order alert sound — PER TENANT (Super Admin configures it for
 * ONE specific tenant, from that tenant's own row — see TenantRow's
 * expandable panel below). Bug found in production (mani tenant): the
 * first version of this stored the sound platform-wide, which forced
 * whatever was uploaded onto EVERY tenant regardless of whether they
 * asked for anything distinct. Deliberately separate from the general
 * sound so a brand-new order (something a merchant/rider genuinely
 * can't afford to miss) sounds distinct from a routine status update —
 * but ONLY for tenants that opt in. Routes through its own Android
 * channel (``bizil_new_order_alert_v1``) — see
 * fcm.py:NEW_ORDER_CHANNEL_ID / NEW_ORDER_TOPICS. A tenant with nothing
 * configured here gets new-order pushes identical to their routine
 * ones — never forced.
 */
function TenantNewOrderSoundPanel({ tenant }) {
    return (
        <SoundUploadCard
            endpoint={`/super/tenants/${tenant.id}/new-order-sound`}
            loudModeEndpoint={`/super/tenants/${tenant.id}/new-order-sound/loud-mode`}
            deployTenantSlug={tenant.slug}
            testidPrefix={`new-order-sound-${tenant.id}`}
            icon={<BellRing size={18} />}
            iconGradient="from-amber-500 to-red-500"
            title={`New order alert — ${tenant.name}`}
            description="A separate, louder/distinct sound specifically for new-order alerts on THIS tenant (merchant: an order just came in; rider: an order is available to claim) — different from the general sound so it can't be confused with a routine status update. Leave unconfigured and this tenant's new-order pushes stay identical to their routine ones. For a genuinely attention-grabbing alert, prefer a louder clip a few seconds long — Android/iOS both play the bundled file's own duration once per push, so the file's length is how long the alert rings for."
            emptyText="No distinct alert sound configured for this tenant — new-order pushes use their general notification sound."
            channelNote={<>the stable <code>bizil_orders_high_v2</code> channel (same as their routine pushes)</>}
            durationTip="A few seconds (2–5s) and louder than the general sound — it needs to cut through a busy kitchen or street noise without being missed."
        />
    );
}
